import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@ndd/ui";
import type { ObservationImageData } from "../../../../shared/observation-types";
import { ObservationImageDeleteDialog } from "./observation-image-delete-dialog";

interface ObservationImageCarouselDialogProps {
  images: ObservationImageData[];
  initialIndex: number;
  open: boolean;
  editable?: boolean;
  deletingImageId?: string | null;
  onOpenChange: (open: boolean) => void;
  onDeleteCurrent?: (image: ObservationImageData) => Promise<void>;
}

export function ObservationImageCarouselDialog({
  images,
  initialIndex,
  open,
  editable = false,
  deletingImageId = null,
  onOpenChange,
  onDeleteCurrent,
}: ObservationImageCarouselDialogProps) {
  const [rawIndex, setRawIndex] = useState(initialIndex);
  const index =
    images.length === 0
      ? 0
      : Math.min(Math.max(rawIndex, 0), images.length - 1);

  useEffect(() => {
    if (!open || images.length === 0) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "ArrowLeft") {
        setRawIndex((prev) => (prev - 1 + images.length) % images.length);
      }
      if (event.key === "ArrowRight") {
        setRawIndex((prev) => (prev + 1) % images.length);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [images.length, open]);

  const image = images[index];
  const hasMultiple = images.length > 1;
  const isDeletingCurrent = image ? deletingImageId === image.id : false;

  function goPrevious() {
    setRawIndex((prev) => (prev - 1 + images.length) % images.length);
  }

  function goNext() {
    setRawIndex((prev) => (prev + 1) % images.length);
  }

  return (
    <Dialog open={open && images.length > 0} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(960px,94vw)] gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-sand-200 bg-sand-100 px-5 py-4">
          <div className="flex items-start justify-between gap-4 pr-8">
            <div>
              <DialogTitle className="text-base text-foreground">
                Observation images
              </DialogTitle>
              <DialogDescription className="text-[12px] text-sand-500">
                {images.length > 0 ? `${index + 1} of ${images.length}` : "No images"}
              </DialogDescription>
            </div>
            {editable && image && onDeleteCurrent && (
              <ObservationImageDeleteDialog
                title="Remove this image?"
                description="This removes the image from the observation. The observation text stays in place."
                actionLabel="Remove image"
                onConfirm={() => onDeleteCurrent(image)}
                trigger={
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isDeletingCurrent}
                    className="h-8 rounded-[10px] border-error px-3 text-xs font-medium text-error hover:bg-error-light hover:text-error"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {isDeletingCurrent ? "Removing..." : "Remove"}
                  </Button>
                }
              />
            )}
          </div>
        </DialogHeader>

        {image && (
          <div className="relative bg-bark px-3 py-4 sm:px-5">
            {hasMultiple && (
              <button
                type="button"
                onClick={goPrevious}
                className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-popover/90 text-popover-foreground shadow-sm transition-colors hover:bg-popover"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}

            <img
              src={image.url}
              alt={`Observation attachment ${index + 1} of ${images.length}`}
              className="mx-auto max-h-[70vh] w-full object-contain"
            />

            {hasMultiple && (
              <button
                type="button"
                onClick={goNext}
                className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-popover/90 text-popover-foreground shadow-sm transition-colors hover:bg-popover"
                aria-label="Next image"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
